import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { sum } from "mathjs"
import { Children } from "../../../../../../../../../components/miscellianous/children"
import { GroupLevel } from "./GroupLevel"
import { useBSLevel } from "../../../../../../../../../core/hooks/beem-shot/useBSLevel"
import { BSParkingListContext } from "../../BSBuildingParkingPage/context/BSParkingListContext"
import { BSBuildingMorphologyContext } from "../../../../tabs/BSBuildingTabs/BSBuildingMorphologyTab/context/BSBuildingMorphologyContext"
import { BSBuildingContext } from "../../../components/context/BSBuildingContext"
import { BSLevelForm } from "../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/BSLevelForm"
import { AssignedParking } from "../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/AssignedParking"
import { BSLevelContext, sortGroupLevelByLevelNumber } from "./BSLevelContext"

export const BSLevelFormContext = React.createContext<BSBuildingLevelFormStore>({} as BSBuildingLevelFormStore)

export function affectNewLevelNumber(groupLevelListWithoutOldGroup: GroupLevel[], isSuperstructure: boolean): GroupLevel[] {
  let currentLevelNumber = 0

  const sortedGroups = [...groupLevelListWithoutOldGroup].sort((a, b) => a.groupNumber - b.groupNumber)

  if (isSuperstructure) {
    return sortedGroups.map((group) => {
      const updatedLevels = group.levelList.map((level) => {
        const newLevel = { ...level, levelNumber: currentLevelNumber }
        currentLevelNumber += 1
        return newLevel
      })

      return { ...group, levelList: updatedLevels }
    })
  } else {
    return groupLevelListWithoutOldGroup.map((group) => {
      const updatedLevels = group.levelList.map((level) => {
        const newLevel = { ...level, levelNumber: currentLevelNumber - 1 }
        currentLevelNumber -= 1
        return newLevel
      })

      return { ...group, levelList: updatedLevels }
    })
  }
}

export function affectNewGroupNumber(groupLevelList: GroupLevel[], isSuperstructure: boolean): GroupLevel[] {
  return groupLevelList.map((groupLevel, index) => {
    const newGroupNumber: number = isSuperstructure ? groupLevelList.length - index : -(index + 1)

    const updatedLevelList = groupLevel.levelList.map((level) => ({
      ...level,
      groupNumber: newGroupNumber,
    }))
    return {
      ...groupLevel,
      groupNumber: newGroupNumber,
      levelList: updatedLevelList,
    }
  })
}

export function BSLevelFormContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { createBSLevelList } = useBSLevel()

  const { fetchAllBSParking } = useContext(BSParkingListContext)
  const { refreshBSBuildingMorphology } = useContext(BSBuildingMorphologyContext)
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { infrastructureParkingList } = useContext(BSParkingListContext)
  const { groupLevelSuperstructureList, groupLevelInfrastructureList, setLevelGroups, setIsLevelUpdated } =
    useContext(BSLevelContext)

  const [groupLevelSuperstructureListForm, setGroupLevelSuperstructureListForm] = useState<GroupLevel[]>([
    ...groupLevelSuperstructureList,
  ])
  const [groupLevelInfrastructureListForm, setGroupLevelInfrastructureListForm] = useState<GroupLevel[]>([
    ...groupLevelInfrastructureList,
  ])
  const [isLevelListSubmitting, setIsLevelListSubmitting] = useState<boolean>(false)
  const [errors, setErrors] = useState<Record<string, string | undefined>>({})
  const [selectedGroupLevel, setSelectedGroupLevel] = useState<GroupLevel | undefined>()

  // UseEffect to keep form synchronized if data from backend changes
  useEffect(() => {
    setGroupLevelSuperstructureListForm(groupLevelSuperstructureList)
  }, [groupLevelSuperstructureList])
  // UseEffect to keep form synchronized if data from backend changes
  useEffect(() => {
    setGroupLevelInfrastructureListForm(groupLevelInfrastructureList)
  }, [groupLevelInfrastructureList])

  const groupLevelsToBSBuildingLevelFormList: BSLevelForm[] = useMemo(
    () => [
      ...groupLevelSuperstructureListForm.flatMap((group) => group.levelList),
      ...groupLevelInfrastructureListForm.flatMap((group) => group.levelList),
    ],
    [groupLevelInfrastructureListForm, groupLevelSuperstructureListForm]
  )

  function updateLevelsState(
    fieldName: keyof BSLevelForm,
    value: number | AssignedParking[],
    groupId: string,
    setFunction: React.Dispatch<React.SetStateAction<GroupLevel[]>>
  ): void {
    setFunction((prev) =>
      prev.map((groupLevel) =>
        groupLevel.id === groupId
          ? {
              ...groupLevel,
              levelList: groupLevel.levelList.map((level) => ({
                ...level,
                [fieldName]: value,
              })),
            }
          : groupLevel
      )
    )
  }

  const setFormFieldValue: (
    fieldName: keyof BSLevelForm,
    value: number | AssignedParking[],
    groupId: string,
    isSuperstructure: boolean
  ) => void = useCallback((fieldName, value, groupId, isSuperstructure) => {
    isSuperstructure
      ? updateLevelsState(fieldName, value, groupId, setGroupLevelSuperstructureListForm)
      : updateLevelsState(fieldName, value, groupId, setGroupLevelInfrastructureListForm)
  }, [])

  const generateUniqueErrorKey: (groupId: string, fieldName: string) => string = useCallback(
    (groupId, fieldName) => `${groupId}-${fieldName}`,
    []
  )

  const validateNonZero: (groupId: string, fieldName: string, fieldValue: number) => void = useCallback(
    (groupId, fieldName, fieldValue) => {
      const errorKey = generateUniqueErrorKey(groupId, fieldName)

      if (fieldValue !== 0) {
        setErrors((prev) => {
          const { [errorKey]: _, ...rest } = prev
          return rest
        })
      }
    },
    [generateUniqueErrorKey]
  )

  // add fields to validate here
  const fieldsToValidate: Array<{
    fieldName: string
    getValue: (group: GroupLevel) => number | undefined
    isValueValid: (value: number | undefined) => boolean
    errorMessage: string
  }> = useMemo(
    () => [
      {
        fieldName: "height",
        getValue: (group) => group.levelList[0].height,
        isValueValid: (value) => value !== 0,
        errorMessage: "La hauteur ne doit pas être égale à zéro",
      },
    ],
    []
  )

  const validateLevelForm: () => boolean = useCallback(() => {
    let isValid = true

    const allGroups = [...groupLevelSuperstructureListForm, ...groupLevelInfrastructureListForm]

    allGroups.forEach((group) => {
      fieldsToValidate.forEach(({ fieldName, getValue, isValueValid, errorMessage }) => {
        const value = getValue(group)
        const errorKey = generateUniqueErrorKey(group.id, fieldName)

        if (!isValueValid(value)) {
          isValid = false

          setErrors((prev) => ({ ...prev, [errorKey]: errorMessage }))
        }
      })
    })

    return isValid
  }, [fieldsToValidate, generateUniqueErrorKey, groupLevelInfrastructureListForm, groupLevelSuperstructureListForm])

  const submitBSBuildingLevelForm: (bsBuildingId: string) => Promise<any> = useCallback(
    (bsBuildingId) =>
      createBSLevelList(bsBuildingId, groupLevelsToBSBuildingLevelFormList)
        .then((newBSBuildingLevelList) => {
          setLevelGroups(newBSBuildingLevelList)
        })
        .then(() => {
          refreshBSBuildingMorphology()
        })
        .finally(() => {
          if (selectedBSBuilding) {
            fetchAllBSParking(selectedBSBuilding.id)
          }
          setIsLevelListSubmitting(false)
        }),
    [
      createBSLevelList,
      groupLevelsToBSBuildingLevelFormList,
      setLevelGroups,
      refreshBSBuildingMorphology,
      selectedBSBuilding,
      fetchAllBSParking,
    ]
  )

  const initializeLevel: (
    levelNumber: number,
    isSuperstructure: boolean,
    groupNumber: number,
    groupId: string,
    defaultInfrastructureParking?: AssignedParking
  ) => BSLevelForm = useCallback(
    (levelNumber, isSuperstructure, groupNumber, groupId, defaultInfrastructureParking) => ({
      superstructure: isSuperstructure,
      levelNumber,
      groupNumber,
      groupId,
      height: 3,
      zonePercentage: 100,
      bsParkingList: defaultInfrastructureParking ? [defaultInfrastructureParking] : [],
    }),
    []
  )

  const initializeBSBuildingGroupLevel: (levelNumberToCreate: number, isSuperstructure: boolean) => void = useCallback(
    (levelNumberToCreate, isSuperstructure) => {
      setIsLevelUpdated(true)
      let levelNumberToCreateWithoutRDC = levelNumberToCreate
      const groupList = isSuperstructure ? groupLevelSuperstructureListForm : groupLevelInfrastructureListForm
      let groupNumber = isSuperstructure ? groupList.length + 1 : -(groupList.length + 1)

      let levelNumber = sum(groupList.map((group) => group.levelList.length))
      // Creation level group
      if (isSuperstructure && groupNumber === 1) {
        // Create rez de chaussée
        const groupRezDeChaussee: GroupLevel = {
          id: crypto.randomUUID(),
          groupNumber,
          levelList: [],
        }
        const rezDeChaussee: BSLevelForm = initializeLevel(0, isSuperstructure, groupNumber, groupRezDeChaussee.id)

        groupRezDeChaussee.levelList.push(rezDeChaussee)

        setGroupLevelSuperstructureListForm([groupRezDeChaussee])

        levelNumber += 1
        groupNumber += 1
        levelNumberToCreateWithoutRDC -= 1
      }

      const groupId = crypto.randomUUID()
      const newLevelList: BSLevelForm[] = []
      for (let i = 0; i < levelNumberToCreateWithoutRDC; i += 1) {
        let currentLevelNumber = levelNumber + i
        if (!isSuperstructure) {
          currentLevelNumber += 1
          currentLevelNumber = -currentLevelNumber
        }

        const defaultInfrastructureParking: AssignedParking | undefined =
          !isSuperstructure && infrastructureParkingList.length >= 1
            ? {
                parkingId: infrastructureParkingList[0].id,
                percentage: 100,
              }
            : undefined

        const newLevel: BSLevelForm = initializeLevel(
          currentLevelNumber,
          isSuperstructure,
          groupNumber,
          groupId,
          defaultInfrastructureParking
        )
        newLevelList.push(newLevel)
      }

      if (levelNumberToCreateWithoutRDC > 0) {
        const newGroup: GroupLevel = {
          id: groupId,
          groupNumber,
          levelList: newLevelList,
        }
        isSuperstructure
          ? setGroupLevelSuperstructureListForm((prev) => [newGroup, ...prev])
          : setGroupLevelInfrastructureListForm((prev) => [...prev, newGroup])
      }
    },
    [
      groupLevelInfrastructureListForm,
      groupLevelSuperstructureListForm,
      infrastructureParkingList,
      initializeLevel,
      setIsLevelUpdated,
    ]
  )

  const removeOldGroupFromMainList: (groupLevelToRemove: GroupLevel, isSuperstructure: boolean) => GroupLevel[] =
    useCallback(
      (groupLevelToRemove, isSuperstructure) =>
        isSuperstructure
          ? groupLevelSuperstructureListForm.filter((group) => group.id !== groupLevelToRemove.id)
          : groupLevelInfrastructureListForm.filter((group) => group.id !== groupLevelToRemove.id),
      [groupLevelSuperstructureListForm, groupLevelInfrastructureListForm]
    )

  const deleteGroupLevel: (groupLevelToDelete: GroupLevel, isSuperstructure: boolean) => void = useCallback(
    (groupLevelToDelete, isSuperstructure) => {
      setIsLevelUpdated(true)

      const groupLevelListWithoutOldGroup = removeOldGroupFromMainList(groupLevelToDelete, isSuperstructure)

      const groupLevelListWithUpdatedLevelNumber = affectNewLevelNumber(groupLevelListWithoutOldGroup, isSuperstructure)

      const sortedGroupLevelList = sortGroupLevelByLevelNumber(groupLevelListWithUpdatedLevelNumber)

      const updatedGroupLevelList = affectNewGroupNumber(sortedGroupLevelList, isSuperstructure)

      isSuperstructure
        ? setGroupLevelSuperstructureListForm(updatedGroupLevelList)
        : setGroupLevelInfrastructureListForm(updatedGroupLevelList)
    },

    [removeOldGroupFromMainList, setIsLevelUpdated]
  )

  const splitGroupLevel: (groupLevelToUngroup: GroupLevel, isSuperstructure: boolean) => void = useCallback(
    (groupLevelToUngroup, isSuperstructure) => {
      setIsLevelUpdated(true)

      const ungroupGroupList: GroupLevel[] = groupLevelToUngroup.levelList.map((level) => {
        const newGroupId = crypto.randomUUID()
        level.groupId = newGroupId
        return {
          id: newGroupId,
          groupNumber: 0, // temporary
          levelList: [level],
        }
      })

      const groupLevelListWithoutOldGroup = removeOldGroupFromMainList(groupLevelToUngroup, isSuperstructure)

      const sortedGroupLevelList = sortGroupLevelByLevelNumber(groupLevelListWithoutOldGroup, ungroupGroupList)

      const updatedGroupLevelList = affectNewGroupNumber(sortedGroupLevelList, isSuperstructure)

      isSuperstructure
        ? setGroupLevelSuperstructureListForm(updatedGroupLevelList)
        : setGroupLevelInfrastructureListForm(updatedGroupLevelList)
    },
    [removeOldGroupFromMainList, setIsLevelUpdated]
  )

  const mergeGroupLevels: (groupLevelToMerge: GroupLevel, selectedGroup: GroupLevel, isSuperstructure: boolean) => void =
    useCallback(
      (groupLevelToMerge, selectedGroup, isSuperstructure) => {
        setIsLevelUpdated(true)

        const original = groupLevelToMerge.levelList[0]
        const source = selectedGroup.levelList[0]

        const updatedLevel = {
          ...original,
          groupId: source.groupId,
          groupNumber: source.groupNumber,
          height: source.height,
        }

        if (source.levelNumber > updatedLevel.levelNumber) {
          isSuperstructure ? selectedGroup.levelList.unshift(updatedLevel) : selectedGroup.levelList.push(updatedLevel)
        } else {
          isSuperstructure ? selectedGroup.levelList.push(updatedLevel) : selectedGroup.levelList.unshift(updatedLevel)
        }

        const groupLevelListWithoutOldGroup = removeOldGroupFromMainList(groupLevelToMerge, isSuperstructure)

        const sortedGroupLevelList = sortGroupLevelByLevelNumber(groupLevelListWithoutOldGroup)

        const updatedGroupLevelList = affectNewGroupNumber(sortedGroupLevelList, isSuperstructure)

        isSuperstructure
          ? setGroupLevelSuperstructureListForm(updatedGroupLevelList)
          : setGroupLevelInfrastructureListForm(updatedGroupLevelList)
      },
      [removeOldGroupFromMainList, setIsLevelUpdated]
    )

  const bSBuildingLevelFormStore: BSBuildingLevelFormStore = useMemo(
    () => ({
      groupLevelSuperstructureListForm,
      setGroupLevelInfrastructureListForm,
      groupLevelInfrastructureListForm,
      setGroupLevelSuperstructureListForm,
      errors,
      isLevelListSubmitting,
      selectedGroupLevel,
      setSelectedGroupLevel,
      setFormFieldValue,
      validateNonZero,
      generateUniqueErrorKey,
      setIsLevelListSubmitting,
      submitBSBuildingLevelForm,
      validateLevelForm,
      initializeBSBuildingGroupLevel,
      splitGroupLevel,
      mergeGroupLevels,
      deleteGroupLevel,
    }),
    [
      groupLevelSuperstructureListForm,
      groupLevelInfrastructureListForm,
      isLevelListSubmitting,
      errors,
      selectedGroupLevel,
      setSelectedGroupLevel,
      setFormFieldValue,
      validateNonZero,
      generateUniqueErrorKey,
      submitBSBuildingLevelForm,
      validateLevelForm,
      initializeBSBuildingGroupLevel,
      splitGroupLevel,
      mergeGroupLevels,
      deleteGroupLevel,
    ]
  )
  return <BSLevelFormContext.Provider value={bSBuildingLevelFormStore}>{children}</BSLevelFormContext.Provider>
}

export interface BSBuildingLevelFormStore {
  groupLevelSuperstructureListForm: GroupLevel[]
  setGroupLevelInfrastructureListForm: Dispatch<SetStateAction<GroupLevel[]>>
  groupLevelInfrastructureListForm: GroupLevel[]
  setGroupLevelSuperstructureListForm: Dispatch<SetStateAction<GroupLevel[]>>
  isLevelListSubmitting: boolean
  setIsLevelListSubmitting: Dispatch<boolean>
  errors: Record<string, string | undefined>
  selectedGroupLevel: GroupLevel | undefined
  setSelectedGroupLevel: React.Dispatch<React.SetStateAction<GroupLevel | undefined>>
  setFormFieldValue(
    fieldName: keyof BSLevelForm,
    value: number | AssignedParking[],
    groupId: string,
    isSuperstructure: boolean
  ): void
  validateNonZero(groupId: string, fieldName: string, fieldValue: number): void
  generateUniqueErrorKey(groupId: string, fieldName: string): string
  submitBSBuildingLevelForm(bsBuildingId: string): Promise<any>
  validateLevelForm(): boolean
  initializeBSBuildingGroupLevel(levelNumberToCreate: number, isSuperstructure: boolean): void
  deleteGroupLevel(groupLevelToDelete: GroupLevel, isSuperstructure: boolean): void
  splitGroupLevel(groupLevelToUngroup: GroupLevel, isSuperstructure: boolean): void
  mergeGroupLevels(groupLevelToMerge: GroupLevel, selectedGroup: GroupLevel, isSuperstructure: boolean): void
}
